import React, { useState } from 'react';
import Seo from '../components/Seo';
import './Contact.css';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.stratosstandard.com';

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const validate = () => {
    const e = {};
    if (!form.name) e.name = 'Required';
    if (!form.email || !/\S+@\S+\.\S+/.test(form.email)) e.email = 'Valid email required';
    if (!form.message) e.message = 'Please enter a message';
    return e;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setLoading(true);
    try {
      await fetch(`${API_BASE}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
    } catch (_) { /* handle gracefully */ }
    setLoading(false);
    setSubmitted(true);
  };

  const set = (k, v) => { setForm(p => ({ ...p, [k]: v })); setErrors(p => ({ ...p, [k]: null })); };

  return (
    <>
      <Seo
        title="Contact Us"
        description="Questions about the Stratos Standard achievement registry or booking a play-based verification session for your child? Get in touch — we're happy to help."
        path="/contact"
      />
      <section className="page-hero">
        <div className="container">
          <p className="eyebrow" style={{ color: '#3ba3e8', position: 'relative' }}>We'd Love to Hear From You</p>
          <h1 className="page-hero-title">Contact Us</h1>
          <p className="page-hero-sub">Questions, feedback, or just want to say hi? Our team is here to help.</p>
        </div>
      </section>

      <section className="section-pad">
        <div className="container contact-layout">
          {/* Info */}
          <div className="contact-info">
            <h2>Get in Touch</h2>
            <p>Whether you're curious about our programs, need help booking, or want to learn more about becoming a SKIES consultant, we're happy to chat.</p>

            <div className="contact-cards">
              {[
                { icon: '📧', title: 'Email', val: 'hello@stratosstandard.com', href: 'mailto:hello@stratosstandard.com' },
                // Phone hidden for now — restore when a real number is available
                // { icon: '📞', title: 'Phone', val: '(555) 123-4567', href: 'tel:+15551234567' },
                { icon: '🕐', title: 'Hours', val: 'Mon to Fri, 9 AM to 6 PM EST', href: null },
              ].map((c, i) => (
                <div key={i} className="contact-card">
                  <span className="cc-icon">{c.icon}</span>
                  <div>
                    <p className="cc-title">{c.title}</p>
                    {c.href
                      ? <a href={c.href} className="cc-val">{c.val}</a>
                      : <p className="cc-val">{c.val}</p>
                    }
                  </div>
                </div>
              ))}
            </div>

            <div className="contact-skies-note">
              <p className="eyebrow" style={{ color: '#3ba3e8', marginBottom: '8px' }}>SKIES Consultants</p>
              <p>Interested in joining our consultant network? Visit our <a href="/become-consultant" style={{ color: '#1a6bb5', fontWeight: 600 }}>Consultant page</a> to learn more about our upcoming program.</p>
            </div>
          </div>

          {/* Form */}
          <div className="contact-form-wrap">
            {submitted ? (
              <div className="contact-success">
                <div className="contact-success-icon">✉️</div>
                <h3>Message Received!</h3>
                <p>Thank you for reaching out. A member of our team will get back to you within 1 to 2 business days.</p>
              </div>
            ) : (
              <form className="contact-form" onSubmit={handleSubmit} noValidate>
                <h3>Send a Message</h3>

                <div className="form-row-2">
                  <div className="form-group">
                    <label className="form-label">Full Name *</label>
                    <input type="text" className={`form-input ${errors.name ? 'input-error' : ''}`}
                      value={form.name} onChange={e => set('name', e.target.value)} placeholder="Jane Smith" />
                    {errors.name && <span className="field-error">{errors.name}</span>}
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email Address *</label>
                    <input type="email" className={`form-input ${errors.email ? 'input-error' : ''}`}
                      value={form.email} onChange={e => set('email', e.target.value)} placeholder="jane@example.com" />
                    {errors.email && <span className="field-error">{errors.email}</span>}
                  </div>
                </div>

                <div className="form-row-2">
                  <div className="form-group">
                    <label className="form-label">Phone (optional)</label>
                    <input type="tel" className="form-input" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="(555) 000-0000" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Subject</label>
                    <select className="form-select" value={form.subject} onChange={e => set('subject', e.target.value)}>
                      <option value="">Select a topic…</option>
                      <option value="booking">Booking / Scheduling</option>
                      <option value="program">Program Questions</option>
                      <option value="consultant">Consultant Inquiry</option>
                      <option value="billing">Billing</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Message *</label>
                  <textarea className={`form-textarea ${errors.message ? 'input-error' : ''}`}
                    value={form.message} onChange={e => set('message', e.target.value)}
                    placeholder="Tell us how we can help…" rows={5} />
                  {errors.message && <span className="field-error">{errors.message}</span>}
                </div>

                <button type="submit" className="btn btn-primary btn-lg" style={{ width: '100%', justifyContent: 'center' }} disabled={loading}>
                  {loading ? <span className="spinner" /> : null}
                  {loading ? 'Sending…' : 'Send Message'}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
